import { useEffect, useRef, useState, useCallback } from 'react';
import { message } from 'antd';

interface TaskUpdate {
  task_id: string;
  task_name: string;
  status: 'processing' | 'success' | 'fail';
  result_info?: string;
  error_msg?: string;
  created_at: string;
}

interface UseTaskMonitorOptions {
  userId: string;
  onTaskUpdate?: (task: TaskUpdate) => void;
  onTaskComplete?: (task: TaskUpdate) => void;
  onTaskFail?: (task: TaskUpdate) => void;
  onTaskNotFound?: (taskId: string) => void; // 任务不存在时的回调
}

export const useTaskMonitor = (options: UseTaskMonitorOptions) => {
  const { userId, onTaskUpdate, onTaskComplete, onTaskFail, onTaskNotFound } = options;
  
  const wsRef = useRef<WebSocket | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [processingTasks, setProcessingTasks] = useState<TaskUpdate[]>([]);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout>();
  const reconnectAttemptsRef = useRef(0);
  const maxReconnectAttempts = 15;
  const isConnectingRef = useRef(false);
  const connectTimeoutRef = useRef<NodeJS.Timeout>(); // 连接超时定时器
  const wsFailedSilently = useRef(false); // WebSocket 静默失败标记，不影响其他功能
  
  // 连接WebSocket
  const connect = useCallback(() => {
    // 防止重复连接
    if (wsRef.current?.readyState === WebSocket.OPEN || 
        wsRef.current?.readyState === WebSocket.CONNECTING ||
        isConnectingRef.current) {
      return;
    }
    
    // 清理旧连接
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const host = window.location.hostname;
    const port = import.meta.env.VITE_WS_PORT || '3001';
    const wsUrl = `${protocol}//${host}:${port}/ws/task-monitor`;
    
    console.log('📡 连接 WebSocket:', wsUrl);
    
    // 清除上一次连接的超时定时器，避免旧定时器影响新连接
    if (connectTimeoutRef.current) {
      clearTimeout(connectTimeoutRef.current);
      connectTimeoutRef.current = undefined;
    }

    // 首次连接无延迟，重连时短延迟避免过快重试
    const connectDelay = reconnectAttemptsRef.current > 0 ? 100 : 0;
    
    setTimeout(() => {
      isConnectingRef.current = true;
      
      try {
        const ws = new WebSocket(wsUrl);
      
      // 连接超时：10秒内未连上则关闭重试
      const localTimeout = setTimeout(() => {
        // 双重检查：ws未连接 且 ws仍是当前活跃连接
        if (ws.readyState !== WebSocket.OPEN && wsRef.current === ws) {
          console.warn('⏱️ WebSocket 连接超时，关闭并重试');
          ws.close();
        }
      }, 10000);
      connectTimeoutRef.current = localTimeout;

      ws.onopen = () => {
        clearTimeout(localTimeout);
        console.log('✅ WebSocket 连接成功');
        setIsConnected(true);
        reconnectAttemptsRef.current = 0;
        isConnectingRef.current = false;
        
        // 查询处理中的任务
        ws.send(JSON.stringify({
          type: 'query_processing',
          userId
        }));
      };
      
      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          handleMessage(data);
        } catch (error) {
          console.error('❌ 解析 WebSocket 消息失败:', error);
        }
      };
      
      ws.onerror = (error) => {
        console.error('❌ WebSocket 错误:', wsUrl);
        isConnectingRef.current = false;
      };
      
      ws.onclose = (event) => {
        clearTimeout(localTimeout);
        console.log('📡 WebSocket 连接关闭, 代码:', event.code, '原因:', event.reason);
        setIsConnected(false);
        wsRef.current = null;
        isConnectingRef.current = false;
        
        // 只有在非正常关闭时才重连（避免手动关闭时重连）
        if (event.code !== 1000 && reconnectAttemptsRef.current < maxReconnectAttempts) {
          // 前3次快速重试(1秒)，之后指数退避，上限60秒
          let delay: number;
          if (reconnectAttemptsRef.current < 3) {
            delay = 1000;
          } else {
            delay = Math.min(1000 * Math.pow(2, reconnectAttemptsRef.current - 3), 60000);
          }
          console.log(`🔄 ${delay}ms 后尝试重连... (${reconnectAttemptsRef.current + 1}/${maxReconnectAttempts})`);
          
          reconnectTimeoutRef.current = setTimeout(() => {
            reconnectAttemptsRef.current++;
            connect();
          }, delay);
        } else if (event.code !== 1000) {
          console.warn('⚠️ WebSocket 重连次数已达上限，等待网络恢复后自动重连');
          wsFailedSilently.current = true;
        }
      };
      
      wsRef.current = ws;
      
      } catch (error) {
        console.error('创建 WebSocket 连接失败:', error);
        isConnectingRef.current = false;
      }
    }, connectDelay);
  }, [userId]);
  
  // 处理消息
  const handleMessage = (data: any) => {
    switch (data.type) {
      case 'task_update':
        handleTaskUpdate(data.data);
        break;
        
      case 'processing_tasks':
        setProcessingTasks(data.data);
        // 自动订阅所有处理中的任务
        data.data.forEach((task: TaskUpdate) => {
          subscribeTask(task.task_id);
        });
        break;
        
      case 'error':
        console.error('WebSocket 错误:', data.message);
        // 检查是否为"任务不存在"错误，如果是则调用回调而不显示错误消息
        if (data.message && data.message.includes('任务不存在') && data.taskId) {
          console.log('🗑️ 任务不存在，可能已被清理:', data.taskId);
          onTaskNotFound?.(data.taskId);
        } else if (data.message && !data.message.includes('任务不存在')) {
          // 只显示非"任务不存在"的错误
          message.error(data.message);
        }
        break;
        
      default:
        console.warn('未知的消息类型:', data.type);
    }
  };
  
  // 处理任务更新
  const handleTaskUpdate = (task: TaskUpdate) => {
    console.log('📬 任务更新:', task.task_id, task.status);
    
    // 更新处理中任务列表
    setProcessingTasks(prev => {
      if (task.status === 'processing') {
        const exists = prev.find(t => t.task_id === task.task_id);
        if (exists) {
          return prev.map(t => t.task_id === task.task_id ? task : t);
        } else {
          return [...prev, task];
        }
      } else {
        return prev.filter(t => t.task_id !== task.task_id);
      }
    });
    
    // 调用回调
    onTaskUpdate?.(task);
    
    // 任务完成或失败时，取消订阅
    if (task.status === 'success' || task.status === 'fail') {
      console.log('🔕 任务完成，取消订阅:', task.task_id);
      
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({
          type: 'unsubscribe',
          taskId: task.task_id
        }));
      }
      
      if (task.status === 'success') {
        message.success(`任务 ${task.task_name} 已完成`);
        onTaskComplete?.(task);
      } else if (task.status === 'fail') {
        message.error(`任务 ${task.task_name} 失败`);
        onTaskFail?.(task);
      }
    }
  };
  
  // 订阅任务
  const subscribeTask = useCallback((taskId: string) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'subscribe',
        taskId,
        userId
      }));
      console.log('📌 订阅任务:', taskId);
    }
  }, [userId]);
  
  // 取消订阅任务
  const unsubscribeTask = useCallback((taskId: string) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'unsubscribe',
        taskId
      }));
      console.log('📌 取消订阅任务:', taskId);
    }
  }, []);
  
  // 查询处理中的任务
  const queryProcessingTasks = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'query_processing',
        userId
      }));
    }
  }, [userId]);
  
  // 清理连接
  const cleanup = useCallback(() => {
    console.log('🧹 清理 WebSocket 连接');
    
    // 清除重连定时器和连接超时定时器
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = undefined;
    }
    if (connectTimeoutRef.current) {
      clearTimeout(connectTimeoutRef.current);
      connectTimeoutRef.current = undefined;
    }
    
    // 关闭WebSocket连接
    if (wsRef.current) {
      wsRef.current.close(1000, '组件卸载'); // 正常关闭
      wsRef.current = null;
    }
    
    // 重置状态
    setIsConnected(false);
    isConnectingRef.current = false;
    reconnectAttemptsRef.current = 0;
  }, []);

  // 网络恢复时自动重连
  const handleOnline = useCallback(() => {
    console.log('🌐 网络恢复，尝试重连 WebSocket');
    reconnectAttemptsRef.current = 0;
    wsFailedSilently.current = false;
    // 延迟一小段时间再连接，等网络稳定
    setTimeout(() => connect(), 2000);
  }, [connect]);

  // 页面可见性变化时检查连接
  const handleVisibilityChange = useCallback(() => {
    if (document.visibilityState === 'visible' && !wsRef.current) {
      console.log('👁️ 页面可见，检查 WebSocket 连接');
      reconnectAttemptsRef.current = 0;
      wsFailedSilently.current = false;
      connect();
    }
  }, [connect]);

  // 初始化连接
  useEffect(() => {
    connect();
    
    // 监听网络恢复和页面可见性
    window.addEventListener('online', handleOnline);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      cleanup();
      window.removeEventListener('online', handleOnline);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [connect, cleanup, handleOnline, handleVisibilityChange]);
  
  return {
    isConnected,
    processingTasks,
    subscribeTask,
    unsubscribeTask,
    queryProcessingTasks
  };
};
